'use client';

import { createElement, useEffect, useRef } from 'react';
import useIsOverwolfIframe from '../../hooks/use-is-overwolf-iframe';
import { openLinksInNewTab } from '../../utils/external-links';

// Tags this is actually used with; widen as needed.
type RichHtmlTag = 'p' | 'div' | 'span';

interface Props {
  // Raw HTML string, injected via dangerouslySetInnerHTML.
  html: string;
  // Element to render. Defaults to 'div'. Keep it matching the original markup
  // so existing CSS selectors (e.g. `.app-description p`) still apply.
  as?: RichHtmlTag;
  className?: string;
}

// -----------------------------------------------------------------------------
// Renders trusted, pre-sanitized HTML. Inside the Overwolf app shell (the store
// running in an iframe) it rewrites embedded links to open in a new tab, because
// an in-frame navigation to a third-party site is blocked by the shell's
// frame-src CSP. Everywhere else the HTML renders unchanged.
//
// SECURITY: `html` goes straight to dangerouslySetInnerHTML with no sanitizing.
// Never pass anything a user can influence (review text, replies, profile
// fields, query params). To show user content, render it as a JSX child so
// React escapes it, and use `white-space: pre-wrap` for line breaks.
export const RichHtml = ({ html, as = 'div', className }: Props): JSX.Element => {
  const ref = useRef<HTMLElement>(null);
  const { isOverwolfIframe } = useIsOverwolfIframe();

  useEffect(() => {
    if (isOverwolfIframe) {
      openLinksInNewTab(ref.current);
    }
  }, [html, isOverwolfIframe]);

  return createElement(as, {
    ref,
    className,
    dangerouslySetInnerHTML: { __html: html },
  });
};

export default RichHtml;
